import React from 'react';
import { MailIcon } from 'lucide-react';
import { statePrivacyLaws, privacyRights, privacyRequestOptions, PRIVACY_CONTACT_EMAIL, type PrivacyRequestOption } from '../data/statePrivacyLaws';

// State privacy rights disclosures for the Privacy Policy: the rights
// themselves, how to exercise them, verification and timing, appeals, and a
// per-state reference table with each regulator.
export function StatePrivacyRights() {
  return <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-910" data-mp="395g5zr1rra7y">
      <p data-id="element-911" data-mp="34cdi9zjoauzo">
        Residents of several U.S. states have rights over their personal data under their state's consumer privacy
        law. Those states are listed in the table below. <strong data-id="element-912" data-mp="2vd04iuoink95">We honor the rights described in this section
        for all U.S. residents</strong>, regardless of which state you live in.
      </p>

      <h3 className="font-bold text-lg mt-6" data-id="element-913" data-mp="2qcvp77zcgvki">Your rights</h3>
      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2" data-id="element-914" data-mp="2lean130j81tf">
        {privacyRights.map(right => <div key={right.name} data-id="element-915" data-mp="28x8y5lc84pna">
            <dt className="font-semibold text-gray-900" data-id="element-916" data-mp="28w7o9k6y2975">{right.name}</dt>
            <dd className="text-sm mt-1" data-id="element-917" data-mp="2dpaizvngjoox">{right.description}</dd>
          </div>)}
      </dl>
      <p className="text-sm" data-id="element-918" data-mp="1z0e1l7zigzbn">
        Some rights are not available in every state; state-specific differences are noted in the table below.
      </p>

      <h3 className="font-bold text-lg mt-6" data-id="element-919" data-mp="23w38xxhrvkii">Sale of personal data and targeted advertising</h3>
      <p data-id="element-920" data-mp="2rm5wc0aqs2as">
        When you ask to be matched with schools, we disclose your contact details and education preferences to
        educational institutions and their representatives, and we receive compensation. Under several state laws
        this is a "sale" of personal data. We also use cookies and pixels, including the Meta Pixel, for targeted
        advertising and measurement, which some state laws treat as "sharing" or "targeted advertising." You can
        opt out of both at any time using the steps below. We do not knowingly sell or share the personal data of
        consumers under 18.
      </p>
      <p data-id="element-921" data-mp="38xkvcwm126bv">
        We do not collect or process sensitive personal data (such as precise geolocation, government
        identifiers, health information, or financial account details) and do not use personal data for profiling
        that produces legal or similarly significant effects.
      </p>

      <h3 id="exercise-your-rights" className="font-bold text-lg mt-6 scroll-mt-24" data-id="element-922" data-mp="2u86ktl6ptiuw">
        How to exercise your rights
      </h3>
      <p data-id="element-923" data-mp="2q5s1kzz4wh33">Choose the type of request below. This opens a pre-filled email to our privacy team:</p>
      <div className="flex flex-wrap gap-2" data-id="element-924" data-mp="2l8t90lv4ci70">
        {privacyRequestOptions.map(option => <a key={option.type} href={buildMailto(option)} className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3b82f6] focus-visible:ring-offset-2 ${option.type === 'opt-out' ? 'bg-[#1a2332] text-white hover:bg-[#2a374a]' : 'border border-gray-300 text-gray-800 hover:bg-gray-50'}`} data-id="element-925" data-mp="2g9gk7vkot11s">
            <MailIcon className="w-4 h-4" aria-hidden="true" data-id="element-926" data-mp="vrxogqimy2fa" />
            {option.label}
          </a>)}
      </div>
      <p data-id="element-927" data-mp="10rsfqv6bou0v">
        Or email{' '}
        <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`} className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-928" data-mp="3279ygd3cyin">
          {PRIVACY_CONTACT_EMAIL}
        </a>{' '}
        with the subject line "Privacy Request" and the type of request, or write to Media-Matchers LLC, Attn:
        Privacy Requests, 1865 Chestnut Street, San Francisco, CA 94123.
      </p>
      <p data-id="element-929" data-mp="i0j6mn0gs79t">Please include:</p>
      <ul className="list-disc pl-6 space-y-1" data-id="element-930" data-mp="1ofacz68cwkb8">
        <li data-id="element-931" data-mp="1oeig7s30wgjd">Your full name, and the email address and phone number you submitted on our site</li>
        <li data-id="element-932" data-mp="1tdbs5g2nvzhf">Your state of residence</li>
        <li data-id="element-933" data-mp="1tghhmwf7d3do">The right you want to exercise, and for corrections, what should be changed</li>
      </ul>

      <h3 className="font-bold text-lg mt-6" data-id="element-934" data-mp="1r1jfwng3pe8n">Universal opt-out signals</h3>
      <p data-id="element-935" data-mp="1mzplgwwi16gx">
        We treat a Global Privacy Control (GPC) signal sent by your browser as a valid request to opt out of the
        sale and sharing of personal data and of targeted advertising for that browser. If you are known to us,
        for example because you submitted a form, we will also apply the opt-out to your account where we can. To
        opt out across devices, submit a request using the options above.
      </p>

      <h3 className="font-bold text-lg mt-6" data-id="element-936" data-mp="2jodwvadplvhd">Verification and response times</h3>
      <ul className="list-disc pl-6 space-y-2" data-id="element-937" data-mp="2jqhxnjgbaedf">
        <li data-id="element-938" data-mp="2ep11204v1e2n">
          <strong data-id="element-939" data-mp="2eqlutnjcoszg">Verification.</strong> For access, deletion, correction, and portability requests, we verify your
          identity by matching the information you provide against the information we hold, and may ask a
          follow-up question. We do not require an account. Opt-out requests do not require verification, though we
          may ask for information needed to locate your records.
        </li>
        <li data-id="element-940" data-mp="3cac21hyjjkdh">
          <strong data-id="element-941" data-mp="3c8rguiy49dsa">Timing.</strong> We will confirm receipt within 10 business days and respond within 45 days.
          Where reasonably necessary, we may extend this by an additional 45 days and will tell you why. Opt-out
          requests are processed as soon as feasible and no later than 15 business days.
        </li>
        <li data-id="element-942" data-mp="2tbzq5og8vwe7">
          <strong data-id="element-943" data-mp="2tcj8355waks8">Cost.</strong> Requests are free. You may make at least two access or portability requests in a
          12-month period free of charge.
        </li>
        <li data-id="element-944" data-mp="2fii9cbey0xrv">
          <strong data-id="element-945" data-mp="2kjpfk61ftowf">Authorized agents.</strong> You may designate an authorized agent to submit a request on your
          behalf. We will ask the agent for written, signed permission from you and may ask you to verify your
          identity directly with us. A parent or guardian may submit requests on behalf of a minor child.
        </li>
      </ul>

      <h3 className="font-bold text-lg mt-6" data-id="element-946" data-mp="1rhokyyewqplq">How to appeal</h3>
      <p data-id="element-947" data-mp="1mpn1arfetwlo">
        If we decline to take action on your request, you may appeal by replying to our decision or emailing{' '}
        <a href={buildMailto(privacyRequestOptions.find(o => o.type === 'appeal')!)} className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-948" data-mp="1hs51bqjderpt">
          {PRIVACY_CONTACT_EMAIL}
        </a>{' '}
        with the subject line "Privacy Request: Appeal" within a reasonable time after our decision. We will
        respond in writing within 45 days, explaining any action taken and the reasons for our decision. If your
        appeal is denied, you may contact the regulator for your state listed below.
      </p>

      <h3 className="font-bold text-lg mt-6" data-id="element-949" data-mp="1vkl0uhd2c7lz">State-by-state reference</h3>
      <div className="overflow-x-auto rounded-md border border-gray-200" data-id="element-950" data-mp="p3zf0zco4unv">
        <table className="min-w-[640px] w-full text-sm text-left" data-id="element-951" data-mp="k2a37hhmgcyq">
          <caption className="sr-only" data-id="element-952" data-mp="2vqomik88fzdf">State consumer privacy laws, notes, and regulator contacts</caption>
          <thead className="bg-gray-50 text-gray-900" data-id="element-953" data-mp="30pi2cjazs2gr">
            <tr data-id="element-954" data-mp="30q19d4z3t6g0">
              <th scope="col" className="px-4 py-3 font-semibold" data-id="element-955" data-mp="30nf3ravrx10f">State</th>
              <th scope="col" className="px-4 py-3 font-semibold" data-id="element-956" data-mp="35njml7k5m7tt">Law and notes</th>
              <th scope="col" className="px-4 py-3 font-semibold" data-id="element-957" data-mp="3agcq5s3vza4b">Regulator</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-958" data-mp="3fe4lkw3qped9">
            {statePrivacyLaws.map(law => <tr key={law.state} className="align-top" data-id="element-959" data-mp="3jeg1xwp2ksix">
                <th scope="row" className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap" data-id="element-960" data-mp="3of2nn8umbbt7">
                  {law.state}
                </th>
                <td className="px-4 py-3" data-id="element-961" data-mp="3tcckh9kc6pr2">
                  <span data-id="element-962" data-mp="1u24ggy47sva3">{law.law}</span>
                  {law.notes && <span className="block mt-1 text-gray-500" data-id="element-963" data-mp="1u3flt2db6b3b">{law.notes}</span>}
                </td>
                <td className="px-4 py-3" data-id="element-964" data-mp="1z2igbsvnwyx1">
                  <a href={law.regulatorUrl} target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-965" data-mp="4rse0zn4we8v">
                    {law.regulatorName}
                  </a>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <p className="text-sm" data-id="element-966" data-mp="3hyameux24opl">
        California residents can find additional disclosures in our{' '}
        <a href="/ca-notice" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-967" data-mp="3d0sixk1t71oz">
          California Notice at Collection
        </a>
        .
      </p>
    </div>;
}
function buildMailto(option: PrivacyRequestOption): string {
  const body = [`Request type: ${option.label}`, '', 'Full name:', 'Email address used on the site:', 'Phone number used on the site:', 'State of residence:', '', 'Details of my request:'].join('\n');
  return `mailto:${PRIVACY_CONTACT_EMAIL}?subject=${encodeURIComponent(option.subject)}&body=${encodeURIComponent(body)}`;
}